/**
 * The visible rules of a spelling bee, mirrored from `backend/apps/puzzletypes/spelling_bee/
 * hive.py`. The dictionary is not here and never will be: whether a word exists is only known
 * to the server, at submission.
 */
import {
  LETTER_COUNT,
  MIN_LENGTH,
  PANGRAM_BONUS,
  type SpellingBeeDefinition,
  type WordIssue,
} from "./types";

/** Mirrors the server's `normalize_word`: accents removed, surrounding spaces trimmed, uppercase. */
export function normalizeWord(word: string): string {
  return word
    .trim()
    .normalize("NFD")
    .replace(/\p{Mn}/gu, "")
    .normalize("NFC")
    .toUpperCase();
}

export interface Hive {
  letters: Set<string>;
  centre: string;
  /** The six letters around the centre, in the definition's order. */
  outer: string[];
}

/** The hive of a definition, or null when its letters do not make one. */
export function readHive(definition: SpellingBeeDefinition): Hive | null {
  const letters = typeof definition.letters === "string" ? definition.letters : "";
  const centre = typeof definition.centre === "string" ? definition.centre : "";
  const set = new Set(letters);
  if (!/^[A-Z]{7}$/.test(letters) || set.size !== LETTER_COUNT) return null;
  if (centre.length !== 1 || !set.has(centre)) return null;
  return { letters: set, centre, outer: [...letters].filter((ch) => ch !== centre) };
}

/** Why `word` (already normalised) breaks a visible rule, or null when it keeps them all. */
export function wordIssue(hive: Hive, word: string): WordIssue | null {
  if (!word || [...word].some((ch) => !hive.letters.has(ch))) return "letter_not_allowed";
  if (word.length < MIN_LENGTH) return "too_short";
  if (!word.includes(hive.centre)) return "missing_centre";
  return null;
}

export function isPangram(hive: Hive, word: string): boolean {
  const used = new Set(word);
  return [...hive.letters].every((ch) => used.has(ch));
}

/** One point for a four-letter word, one per letter beyond; a pangram earns seven more. */
export function points(hive: Hive, word: string): number {
  const base = word.length === MIN_LENGTH ? 1 : word.length;
  return base + (isPangram(hive, word) ? PANGRAM_BONUS : 0);
}
